import * as React from 'react';
import { useState } from 'react';
import { useTranslations } from '../../../store/translations/hooks';
import { AutocompleteTextField, TextInputField, ComboField } from '../../../components/Forms/fields';
import { Slider } from '@material-ui/core';
import styled from 'styled-components';
import { TK } from '../../../store/translations/translationKeys';
import { ClientsLoginSettingsData, SelfSchedulingLayout, SelfSchedulingSettingsData } from '../../../models';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Form } from 'reactstrap';
import { CheckInputField } from '../../../components/Forms/fields/CheckInputField';
import { useDispatch, useSelector } from 'react-redux';
import { getEntitiesDataItems } from '../../../store/entities/selectors';
import { ProfessionalEntity } from '../../../store/entities/state';
import { asSnake } from '../../../shared/string';
import { useGeneralSettings, useRoomsSet, useServicesSet } from '../../../store/settings/hooks';
import { serviceId, serviceName } from '../../../shared/appointmint/appointment';
import { getLoggedUserCompanies } from '../../../store/session/selectors';
import { alertSuccess } from '../../../store/alerts/actions';

interface ConfigurationTabProps {
    id: string;
    values: ClientsLoginSettingsData;
    onSelfSchedulingChanged: (selfScheduling: Partial<SelfSchedulingSettingsData>) => void;
}

const SliderWrapper = styled.div`
    padding-top: var(--spacer-M);
    margin-left: var(--spacer-M);
    margin-right: var(--spacer-M);

    .MuiSlider-valueLabel > span {
        width: auto;
        height: auto;
        transform: translateX(0%) translateY(100%);
        border-radius: 4px;
        padding: 2px 6px;

        > span {
            transform: none;
            white-space: nowrap;
        }
    }
`;

export const ConfigurationTab: React.FC<ConfigurationTabProps> = ({ id, values, onSelfSchedulingChanged }) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const capabilitiesSet = useServicesSet();
    const generalSettings = useGeneralSettings();
    const rooms = useRoomsSet();
    const professionals = useSelector(getEntitiesDataItems<ProfessionalEntity>('professionals'));
    const [urlDropdownOpen, setUrlDropdownOpen] = useState(false);
    const [companyId] = useSelector(getLoggedUserCompanies);

    const appointmentTypeRestrictions = values?.selfScheduling?.professionalsPerAppointmentType ?? {};
    const isAllowAllByDefault = values?.selfScheduling?.allowAllByDefault ?? !Object.keys(appointmentTypeRestrictions).length;
    const daysFormat = (v: number) => v === 0 ? t(TK.today) : v < 30 ? `${v} ${t(TK.days)}` : `${v/30} ${t(TK.months)}`
    const rangeValueFormat = (value: number, min: number, max: number) =>
        min === max ? t(TK.never) : daysFormat(value)

    const daysMarks = [
        { value: 0, label: t(TK.today) },
        ...Array.from({ length: 29 }, (_, i) => ({ value: i + 1, label: undefined })),
        ...[30, 60, 90, 120, 150, 180].map(v => ({ value: v, label: daysFormat(v) })),
    ];

    return (
        <>
            <div style={{textAlign: 'end'}}>
                <Dropdown isOpen={urlDropdownOpen} toggle={() => setUrlDropdownOpen(o => !o)} style={{display: 'inline-block'}}>
                    <DropdownToggle color='link' caret>
                        {t(TK.copyAccessUrl)}
                    </DropdownToggle>
                    <DropdownMenu right>
                        <DropdownItem onClick={() => { navigator.clipboard.writeText(`${import.meta.env.VITE_CLIENT_PORTAL_BASE_URL}/?companyId=${companyId}`); dispatch(alertSuccess(t(TK.copiedToClipboard))); }}>
                            {t(TK.clientsDirectLoginPage)}
                        </DropdownItem>
                        <DropdownItem onClick={() => { navigator.clipboard.writeText(`${import.meta.env.VITE_SCHEDULE_BASE_URL}#${companyId}`); dispatch(alertSuccess(t(TK.copiedToClipboard))); }}>
                            {t(TK.anonymousSelfSchedulingPage)}
                        </DropdownItem>
                        <DropdownItem onClick={() => { navigator.clipboard.writeText(`${import.meta.env.VITE_SCHEDULE_BASE_URL}?headless=true#${companyId}`); dispatch(alertSuccess(t(TK.copiedToClipboard))); }}>
                            {t(TK.anonymousSelfSchedulingPage)} ({t(TK.forLandingPageIntegration)})
                        </DropdownItem>
                    </DropdownMenu>
                </Dropdown>
            </div>

            <Form style={{display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-M)'}}>

                <ComboField
                    id={`${id}-layout`}
                    label={t(TK.selfSchedulingLayout)}
                    value={String(values?.selfScheduling?.layout ?? SelfSchedulingLayout.ServicesList)}
                    options={[
                        { value: String(SelfSchedulingLayout.ServicesList), label: t(TK.selfSchedulingLayoutServicesList) },
                        { value: String(SelfSchedulingLayout.ServicesDropdown), label: t(TK.selfSchedulingLayoutServicesDropdown) },
                    ]}
                    onChange={layout => onSelfSchedulingChanged({layout: Number(layout) as SelfSchedulingLayout})}
                />
                
                <CheckInputField
                    id={`${id}-self-scheduling-allow-all-by-default`}
                    label={t(TK.allowAllRoomsServicesAndProfessionalsByDefault)}
                    value={isAllowAllByDefault}
                    onChange={allowAllByDefault => onSelfSchedulingChanged({allowAllByDefault})}/>

                {!isAllowAllByDefault && <>
                    <AutocompleteTextField
                        id={`${id}-rooms`}
                        label={t(TK.rooms)}
                        multiSelect
                        options={rooms}
                        values={values?.selfScheduling?.defaultRooms}
                        onValuesChanged={defaultRooms => onSelfSchedulingChanged({defaultRooms})}
                    />

                    <AutocompleteTextField
                        id={`${id}-self-scheduling-appointment-types`}
                        label={t(TK.appointmentTypesAvailable)}
                        values={Object.keys(appointmentTypeRestrictions).map(k => serviceName(k, generalSettings))}
                        multiSelect={true}
                        options={capabilitiesSet.map(k => serviceName(k, generalSettings))}
                        onValuesChanged={displayNames => onSelfSchedulingChanged({professionalsPerAppointmentType: Object.fromEntries(displayNames.map(name => {
                            const type = serviceId(name, generalSettings)
                            return [type, appointmentTypeRestrictions[type] ?? (professionals ?? []).filter(p => p.capabilities?.includes(type)).map(p => p.id)]
                        }))})}
                    />

                    {Object.keys(appointmentTypeRestrictions).map(type => (
                        <AutocompleteTextField
                            key={type}
                            id={`${id}-self-scheduling-${asSnake(type)}-professionals`}
                            label={`${serviceName(type, generalSettings)} - ${t(TK.professionalsAvailable)}`}
                            values={(appointmentTypeRestrictions[type] ?? [])
                                .map(professionalId => professionals.find(p => p.id === professionalId)?.name)
                                .filter((name): name is string => !!name)}
                            multiSelect={true}
                            options={professionals.map(p => p.name)}
                            onValuesChanged={professionalsNames => onSelfSchedulingChanged({professionalsPerAppointmentType: {
                                ...appointmentTypeRestrictions,
                                [type]: professionalsNames
                                    .map(name => professionals.find(p => p.name === name)?.id ?? '')
                                    .filter(professionalId => professionalId.length)
                            }})}
                        />
                    ))}
                </>}

                <CheckInputField
                    id={`${id}-anonymous-self-scheduling-enabled`}
                    label={t(TK.allowAnonymousSelfScheduling)}
                    value={values?.selfScheduling?.allowAnonymous}
                    onChange={allowAnonymous => onSelfSchedulingChanged({allowAnonymous})}/>

                <CheckInputField
                    id={`${id}-prevent-calendar-holes`}
                    label={t(TK.avoidCalendarHoles)}
                    value={values?.selfScheduling?.preventCalendarHoles}
                    onChange={preventCalendarHoles => onSelfSchedulingChanged({preventCalendarHoles})}/>

                <hr style={{width: '100%'}} />

                <div>
                    <label style={{display: 'block', marginBottom: 'var(--spacer-S)', fontWeight: 500}}>
                        {t(TK.bookingWindow)}
                    </label>
                    <SliderWrapper>
                    <Slider
                        value={[
                            values?.selfScheduling?.minimumDaysToSchedule ?? 1,
                            values?.selfScheduling?.maximumDaysToSchedule ?? 30,
                        ]}
                        min={0}
                        max={180}
                        step={null}
                        marks={daysMarks}
                        valueLabelDisplay="on"
                        valueLabelFormat={(value) => rangeValueFormat(
                            value,
                            values?.selfScheduling?.minimumDaysToSchedule ?? 1,
                            values?.selfScheduling?.maximumDaysToSchedule ?? 30,
                        )}
                        onChange={(_, newValue) => {
                            const [min, max] = newValue as number[];
                            onSelfSchedulingChanged({
                                minimumDaysToSchedule: min > max ? max : min,
                                maximumDaysToSchedule: min > max ? min : max,
                            });
                        }}
                    />
                    </SliderWrapper>
                </div>

                <div>
                    <label style={{display: 'block', marginBottom: 'var(--spacer-S)', fontWeight: 500}}>
                        {t(TK.minimumDaysToCancel)}
                    </label>
                    <SliderWrapper>
                        <Slider
                            value={[values?.selfScheduling?.minimumDaysToCancel ?? 1, 180]}
                            min={0} max={180} step={null} marks={daysMarks}
                            valueLabelDisplay="on" valueLabelFormat={(value) => rangeValueFormat(
                                value,
                                values?.selfScheduling?.minimumDaysToCancel ?? 1,
                                180,
                            )}
                            onChange={(_, v) => onSelfSchedulingChanged({minimumDaysToCancel: (v as number[])[0]})}
                        />
                    </SliderWrapper>
                </div>

                <div>
                    <label style={{display: 'block', marginBottom: 'var(--spacer-S)', fontWeight: 500}}>
                        {t(TK.minimumDaysToAutoApproveSchedule)}
                    </label>
                    <SliderWrapper>
                        <Slider
                            value={[values?.selfScheduling?.minimumDaysToAutoApproveSchedule ?? 2, 180]}
                            min={0} max={180} step={null} marks={daysMarks}
                            valueLabelDisplay="on" valueLabelFormat={(value) => rangeValueFormat(
                                value,
                                values?.selfScheduling?.minimumDaysToAutoApproveSchedule ?? 2,
                                180,
                            )}
                            onChange={(_, v) => onSelfSchedulingChanged({minimumDaysToAutoApproveSchedule: (v as number[])[0]})}
                        />
                    </SliderWrapper>
                </div>

                <div>
                    <label style={{display: 'block', marginBottom: 'var(--spacer-S)', fontWeight: 500}}>
                        {t(TK.minimumDaysToAutoApproveCancel)}
                    </label>
                    <SliderWrapper>
                        <Slider
                            value={[values?.selfScheduling?.minimumDaysToAutoApproveCancel ?? 2, 180]}
                            min={0} max={180} step={null} marks={daysMarks}
                            valueLabelDisplay="on" valueLabelFormat={(value) => rangeValueFormat(
                                value,
                                values?.selfScheduling?.minimumDaysToAutoApproveCancel ?? 2,
                                180,
                            )}
                            onChange={(_, v) => onSelfSchedulingChanged({minimumDaysToAutoApproveCancel: (v as number[])[0]})}
                        />
                    </SliderWrapper>
                </div>

                <hr style={{width: '100%'}} />

                <TextInputField
                    id={`${id}-privacy-policy-url`}
                    label={`URL - ${t(TK.privacyPolicy)}`}
                    value={values?.selfScheduling?.privacyPolicyUrl ?? ''}
                    onChange={privacyPolicyUrl => onSelfSchedulingChanged({privacyPolicyUrl})}
                />
            </Form>
        </>
    );
};
